import { ModelSelection } from '../model';
import { MagicProvider } from './types';
import { MAGIC_BACKEND_ENABLED } from './config';
import { createBackendProvider } from './backendProvider';
import { createGeminiProvider } from './geminiProvider';
import { createFakeProvider } from './fakeProvider';

// Runtime inputs the registry needs to pick a provider.
export type ProviderContext = {
  // DIRECT path (legacy, MAGIC_BACKEND_ENABLED off): the user's own Google AI
  // Studio key from localStorage.
  apiKey: string | null;
  // BACKEND path: the stored key + model the user picked in the studio. The
  // provider (Gemini / OpenAI) is carried by the selection because the backend
  // derives it from the key.
  selection: ModelSelection | null;
};

// Single place that decides which provider serves a generation:
//   1. backend proxy   — when MAGIC_BACKEND_ENABLED and a key+model is chosen;
//   2. Gemini direct   — legacy: the user connected their own key and it
//                        supports the chosen model;
//   3. fake demo       — otherwise, so the studio stays explorable.
export function getProvider(modelId: string, ctx: ProviderContext): MagicProvider {
  if (MAGIC_BACKEND_ENABLED) {
    if (ctx.selection) {
      return createBackendProvider(ctx.selection);
    }
    // Unreachable in practice: the modal refuses to generate without a
    // selection (it points the user at the key form instead). Kept so the
    // function stays total.
    return createFakeProvider();
  }
  if (ctx.apiKey) {
    const gemini = createGeminiProvider(ctx.apiKey);
    if (gemini.supportsModel(modelId)) {
      return gemini;
    }
  }
  return createFakeProvider();
}

// Whether the active configuration runs real generations (vs the demo fallback).
// Used by the modal to switch its footer / messaging.
export function isRealGeneration(ctx: ProviderContext): boolean {
  return MAGIC_BACKEND_ENABLED ? !!ctx.selection : !!ctx.apiKey;
}

// Coarse label of which path a generation will take, for product logging:
//   'backend' — the asset_lab AI proxy (the user's key, held server-side);
//   'key'     — the user's own Google AI Studio key (direct from the browser);
//   'demo'    — the fake provider (no real generation).
// Derived from the same getProvider decision so it can never drift from it.
export type GenerationPath = 'backend' | 'key' | 'demo';
export function generationPath(modelId: string, ctx: ProviderContext): GenerationPath {
  const id = getProvider(modelId, ctx).id;
  if (id === 'backend') return 'backend';
  if (id === 'gemini') return 'key';
  return 'demo';
}
